import { useCreateEventVenue, type CreateEventVenueRequest } from "@/api/event/useCreateEventVenue";
import { useDeleteEventVenue } from "@/api/event/useDeleteEventVenue";
import { useGetEventVenues, type EventVenueResponse } from "@/api/event/useGetEventVenues";
import { useUpdateEventVenue } from "@/api/event/useUpdateEventVenue";
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconArrowsSort, IconChevronDown, IconChevronUp, IconEdit, IconTrash } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import type { Location } from "./types";
import { showErrorNotification } from "./utils/errorNotifications";

interface VenueSectionProps {
  locations: Location[];
}

type SortField = "eventCount" | "reviewCount" | "avgScore";
type SortDirection = "asc" | "desc";

export const VenueSection = ({ locations }: VenueSectionProps) => {
  const { data: venues, isLoading } = useGetEventVenues();
  const createVenue = useCreateEventVenue();
  const updateVenue = useUpdateEventVenue();
  const deleteVenue = useDeleteEventVenue();

  const [editingVenue, setEditingVenue] = useState<EventVenueResponse | null>(null);
  const [deleteConfirmVenue, setDeleteConfirmVenue] = useState<EventVenueResponse | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const sortedVenues = useMemo(() => {
    if (!venues || !sortField) return venues;
    return [...venues].sort((a, b) => {
      const aVal = a[sortField] ?? 0;
      const bVal = b[sortField] ?? 0;
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [venues, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <IconArrowsSort size={14} style={{ marginLeft: 4, opacity: 0.4 }} />;
    }
    return sortDirection === "asc" ? (
      <IconChevronUp size={14} style={{ marginLeft: 4 }} />
    ) : (
      <IconChevronDown size={14} style={{ marginLeft: 4 }} />
    );
  };

  const createForm = useForm<CreateEventVenueRequest>({
    initialValues: {
      name: "",
      address: "",
      locationId: undefined,
      type: "RESTAURANT",
    },
  });

  const editForm = useForm<CreateEventVenueRequest>({
    initialValues: {
      name: "",
      address: "",
      locationId: undefined,
      type: "RESTAURANT",
    },
  });

  const handleCreate = (values: CreateEventVenueRequest) => {
    createVenue.mutate(values, {
      onSuccess: () => {
        notifications.show({
          title: "성공",
          message: "베뉴가 성공적으로 생성되었습니다",
          color: "green",
        });
        createForm.reset();
      },
      onError: (error) => {
        showErrorNotification(error, "베뉴 생성 실패");
      },
    });
  };

  const handleEdit = (venue: EventVenueResponse) => {
    setEditingVenue(venue);
    editForm.setValues({
      name: venue.name,
      address: venue.address,
      locationId: venue.locationId ?? undefined,
      type: venue.type,
    });
  };

  const handleUpdate = (values: CreateEventVenueRequest) => {
    if (!editingVenue) return;

    updateVenue.mutate(
      { id: editingVenue.id, data: values },
      {
        onSuccess: () => {
          notifications.show({
            title: "성공",
            message: "베뉴가 성공적으로 수정되었습니다",
            color: "green",
          });
          setEditingVenue(null);
          editForm.reset();
        },
        onError: (error) => {
          showErrorNotification(error, "베뉴 수정 실패");
        },
      }
    );
  };

  const handleDelete = () => {
    if (!deleteConfirmVenue) return;

    deleteVenue.mutate(deleteConfirmVenue.id, {
      onSuccess: () => {
        notifications.show({
          title: "성공",
          message: "베뉴가 성공적으로 삭제되었습니다",
          color: "green",
        });
        setDeleteConfirmVenue(null);
      },
      onError: (error) => {
        showErrorNotification(error, "베뉴 삭제 실패");
      },
    });
  };

  const typeOptions = [
    { value: "RESTAURANT", label: "레스토랑" },
    { value: "AFTER_PARTY", label: "애프터파티" },
  ];

  const locationOptions = locations.map((loc) => ({
    value: String(loc.id),
    label: loc.name,
  }));

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Title order={2} c="black" mb="md">
        베뉴 관리
      </Title>

      <Stack gap="lg" style={{ flex: 1, overflow: "hidden" }}>
        {/* Create Form */}
        <form onSubmit={createForm.onSubmit(handleCreate)}>
          <Stack
            gap="xs"
            p="md"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.02)",
              borderRadius: "8px",
              border: "1px solid rgba(0, 0, 0, 0.1)",
            }}
          >
            <Text size="sm" fw={600} c="black">
              새 베뉴 추가
            </Text>
            <Group grow>
              <Select
                label="타입"
                data={typeOptions}
                styles={{ option: { color: "black" } }}
                {...createForm.getInputProps("type")}
              />
              <Select
                label="로케이션 (선택사항)"
                placeholder="선택하세요"
                data={locationOptions}
                clearable
                styles={{ option: { color: "black" } }}
                value={createForm.values.locationId ? String(createForm.values.locationId) : null}
                onChange={(value) =>
                  createForm.setFieldValue("locationId", value ? Number(value) : undefined)
                }
              />
            </Group>
            <Group grow>
              <TextInput
                label="이름"
                placeholder="베뉴 이름"
                {...createForm.getInputProps("name")}
              />
              <TextInput
                label="주소"
                placeholder="베뉴 주소"
                {...createForm.getInputProps("address")}
              />
            </Group>
            <Button type="submit" loading={createVenue.isPending}>
              추가
            </Button>
          </Stack>
        </form>

        {/* Venues List */}
        <ScrollArea style={{ flex: 1 }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ color: "black" }}>타입</Table.Th>
                <Table.Th style={{ color: "black" }}>이름</Table.Th>
                <Table.Th style={{ color: "black" }}>주소</Table.Th>
                <Table.Th style={{ color: "black" }}>로케이션</Table.Th>
                <Table.Th
                  style={{ color: "black", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("eventCount")}
                >
                  <Group gap={0}>이벤트 수<SortIcon field="eventCount" /></Group>
                </Table.Th>
                <Table.Th
                  style={{ color: "black", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("reviewCount")}
                >
                  <Group gap={0}>리뷰 수<SortIcon field="reviewCount" /></Group>
                </Table.Th>
                <Table.Th
                  style={{ color: "black", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("avgScore")}
                >
                  <Group gap={0}>평균 점수<SortIcon field="avgScore" /></Group>
                </Table.Th>
                <Table.Th style={{ color: "black" }}>액션</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={8} style={{ textAlign: "center", color: "black" }}>
                    로딩 중...
                  </Table.Td>
                </Table.Tr>
              ) : sortedVenues && sortedVenues.length > 0 ? (
                sortedVenues.map((venue) => (
                  <Table.Tr key={venue.id}>
                    <Table.Td style={{ color: "black" }}>
                      {venue.type === "RESTAURANT" ? "레스토랑" : "애프터파티"}
                    </Table.Td>
                    <Table.Td style={{ color: "black" }}>{venue.name}</Table.Td>
                    <Table.Td style={{ color: "black" }}>{venue.address}</Table.Td>
                    <Table.Td style={{ color: "black" }}>{venue.locationName || "-"}</Table.Td>
                    <Table.Td style={{ color: "black" }}>{venue.eventCount}</Table.Td>
                    <Table.Td style={{ color: "black" }}>{venue.reviewCount}</Table.Td>
                    <Table.Td style={{ color: "black" }}>
                      {venue.avgScore !== null ? venue.avgScore.toFixed(1) : "-"}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <ActionIcon variant="light" color="blue" onClick={() => handleEdit(venue)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="red"
                          onClick={() => setDeleteConfirmVenue(venue)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={8} style={{ textAlign: "center", color: "black" }}>
                    등록된 베뉴가 없습니다
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Stack>

      {/* Edit Modal */}
      <Modal
        opened={!!editingVenue}
        onClose={() => {
          setEditingVenue(null);
          editForm.reset();
        }}
        title={
          <Text fw={700} c="black">
            베뉴 수정
          </Text>
        }
        size="lg"
      >
        <form onSubmit={editForm.onSubmit(handleUpdate)}>
          <Stack gap="md">
            <Select
              label="타입"
              data={typeOptions}
              color="black"
              styles={{ option: { color: "black" }, label: { color: "black" } }}
              {...editForm.getInputProps("type")}
            />
            <Select
              label="지역 (선택사항)"
              placeholder="선택하세요"
              data={locationOptions}
              clearable
              styles={{ option: { color: "black" }, label: { color: "black" } }}
              value={editForm.values.locationId ? String(editForm.values.locationId) : null}
              onChange={(value) =>
                editForm.setFieldValue("locationId", value ? Number(value) : undefined)
              }
            />
            <TextInput
              label="이름"
              placeholder="베뉴 이름"
              styles={{ label: { color: "black" } }}
              {...editForm.getInputProps("name")}
            />
            <TextInput
              label="주소"
              placeholder="베뉴 주소"
              styles={{ label: { color: "black" } }}
              {...editForm.getInputProps("address")}
            />
            <Group justify="flex-end" mt="md">
              <Button
                variant="outline"
                onClick={() => {
                  setEditingVenue(null);
                  editForm.reset();
                }}
              >
                취소
              </Button>
              <Button type="submit" loading={updateVenue.isPending}>
                저장
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={!!deleteConfirmVenue}
        onClose={() => setDeleteConfirmVenue(null)}
        title="베뉴 삭제 확인"
      >
        <Text c="black" mb="md">
          정말로 "{deleteConfirmVenue?.name}" 베뉴를 삭제하시겠습니까?
        </Text>
        <Group justify="flex-end">
          <Button variant="outline" onClick={() => setDeleteConfirmVenue(null)}>
            취소
          </Button>
          <Button color="red" onClick={handleDelete} loading={deleteVenue.isPending}>
            삭제
          </Button>
        </Group>
      </Modal>
    </div>
  );
};
